import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spin, message } from "antd";
import api from "services/api";
import StepCheckout from "pages/RegisterPage/StepCheckout/StepCheckout";
import "./FacilityPaymentPage.css";

const FacilityPaymentPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [applicationData, setApplicationData] = useState(null);
  const [registrationInfo, setRegistrationInfo] = useState(null);
  const [error, setError] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);

  // State that StepCheckout expects
  const [userData, setUserData] = useState({});
  const [errorField, setErrorField] = useState([]);

  useEffect(() => {
    if (!token) {
      setError("Invalid payment link");
      setLoading(false);
      return;
    }

    const params = new URLSearchParams();
    params.append("payment_token", token);

    api.post("camp/get_facility_application_by_token", params)
      .then((res) => {
        const data = res.data;
        if (data.result) {
          const app = data.data.application;
          const regInfo = data.data.registration_info;
          const payConfig = data.data.payment_config;

          setApplicationData(app);
          setRegistrationInfo(regInfo);

          // Pre-fill billing from application data
          setUserData({
            bill_first_name: app.contact_first_name || "",
            bill_last_name: app.contact_last_name || "",
            bill_address: app.contact_address || "",
            bill_city: app.contact_city || "",
            bill_state: app.contact_state || "",
            bill_postal_code: app.contact_zip || "",
            email: app.contact_email || "",
            p_cell_phone: app.contact_phone || "",
            total_amount: parseFloat(app.total_amount || 0),
            total_checkout: parseFloat(app.total_amount || 0),
            processing_fee: 0,
            camperInfo: [],
          });
        } else {
          setError(data.msg || "Unable to load payment information");
        }
        setLoading(false);
      })
      .catch((err) => {
        setError("Unable to load payment information. Please try again later.");
        setLoading(false);
      });
  }, [token]);

  const onRegister = (cardType, transactionId, customerId, authCode, locationId) => {
    const params = new URLSearchParams();
    params.append("payment_token", token);
    params.append("transaction_id", transactionId || "");
    params.append("payment_method", cardType || "credit");
    params.append("total_amount", userData.total_checkout || userData.total_amount || "0");
    params.append("authorization_code", authCode || "");

    api.post("camp/pay_facility_application", params)
      .then((res) => {
        const data = res.data;
        if (data.result) {
          setPaymentComplete(true);
          message.success("Payment successful!");
        } else {
          message.error(data.msg || "Payment recording failed");
        }
      })
      .catch((err) => {
        message.error("Failed to record payment. Please contact support.");
      });
  };

  const onPaymentFail = (errorMsg) => {
    message.error(errorMsg || "Payment failed. Please try again.");
  };

  if (loading) {
    return (
      <div className="facility-payment-loading">
        <Spin size="large" />
        <p style={{ marginTop: 16, color: "#888" }}>Loading payment information...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="facility-payment-error">
        <h2>Payment Link Error</h2>
        <p>{error}</p>
        <p style={{ color: "#888", marginTop: 16 }}>
          If you believe this is an error, please contact the administrator.
        </p>
      </div>
    );
  }

  if (paymentComplete) {
    return (
      <div className="facility-payment-success">
        <h1>Payment Successful</h1>
        <p style={{ fontSize: 16 }}>
          Thank you for your payment. Your reservation for{" "}
          <strong>{applicationData?.facility}</strong> on{" "}
          <strong>{applicationData?.event_date}</strong> is now confirmed.
        </p>
        <p style={{ color: "#888", marginTop: 20 }}>
          A confirmation email has been sent to {applicationData?.contact_email}.
        </p>
      </div>
    );
  }

  // Determine merchant from registration info or payment config
  const merchant = registrationInfo?.merchant || "forte";

  // Build the appInfo shape that StepCheckout expects
  const appInfo = {
    registration_info: {
      ...registrationInfo,
      // Override registration_type so StepCheckout doesn't treat this as a facility submission
      registration_type: "individual",
    },
  };

  return (
    <div className="facility-payment-page">
      <div className="facility-payment-header">
        <h1>Complete Your Payment</h1>
        <p>Your facility application has been approved. Please complete payment below.</p>
      </div>

      <div className="facility-payment-summary">
        <h3>Reservation Details</h3>
        <div className="summary-row">
          <span className="summary-label">Facility</span>
          <span className="summary-value">{applicationData?.facility || "N/A"}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">Event Date</span>
          <span className="summary-value">{applicationData?.event_date?.replaceAll("-", "/") || "N/A"}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">Time</span>
          <span className="summary-value">
            {applicationData?.event_start_time || ""} - {applicationData?.event_end_time || ""}
          </span>
        </div>
        <div className="summary-row">
          <span className="summary-label">Hours</span>
          <span className="summary-value">{applicationData?.total_hours || "N/A"}</span>
        </div>
        <div className="summary-row summary-total">
          <span className="summary-label">Total Due</span>
          <span className="summary-value">
            ${parseFloat(applicationData?.total_amount || 0).toFixed(2)}
          </span>
        </div>
      </div>

      <StepCheckout
        appInfo={appInfo}
        userData={userData}
        setUserData={setUserData}
        errorField={errorField}
        setErrorField={setErrorField}
        onRegister={onRegister}
        onPaymentFail={onPaymentFail}
        recordList={[]}
        regType="camp"
        merchant={merchant}
      />
    </div>
  );
};

export default FacilityPaymentPage;
